import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Stack,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ManagementPageLayout, { ManagementPageCard } from '../../components/layout/ManagementPageLayout';
import ManagementPageSkeleton from '../../components/layout/ManagementPageSkeleton';
import TeamMemberCompensationForm from '../../components/employee-payments/TeamMemberCompensationForm';
import TeamMemberService from '../../services/teamMemberService';
import usePermissions from '../../hooks/usePermissions';
import { Permissions } from '../../constants/Permissions';
import { useAuth } from '../../context/AuthContext';
import requestDeduplicator from '../../utils/requestDeduplicator';
import { BRAND } from '../../theme/brandTokens';

const TeamMemberCompensationPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { storeId, teamMemberId } = useParams();
  const { managementUser } = useAuth();
  const { hasPermission, loading: permissionsLoading } = usePermissions(storeId);

  const [loading, setLoading] = useState(true);
  const [teamMemberName, setTeamMemberName] = useState('');
  const [loadError, setLoadError] = useState(null);

  const canManageCompensation = hasPermission(Permissions.EmployeePaymentsSettingsUpdate);
  const canViewOwnCompensation = hasPermission(Permissions.EmployeePaymentsReadOwn);
  const isOwnProfile = managementUser?.id === teamMemberId;
  const canView = canManageCompensation || (canViewOwnCompensation && isOwnProfile);
  const canEdit = canManageCompensation;

  const loadTeamMemberName = useCallback(async () => {
    if (!storeId || !teamMemberId || !canView) {
      setLoading(false);
      return;
    }

    const requestKey = `team-member-compensation-page-${storeId}-${teamMemberId}`;

    return requestDeduplicator.deduplicate(requestKey, async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const member = await TeamMemberService.getTeamMember(teamMemberId, storeId);
        setTeamMemberName(member?.name || '');
      } catch (error) {
        console.error('Failed to load team member:', error);
        setLoadError(t('employeePayments.compensation.error.loading'));
      } finally {
        setLoading(false);
      }
    });
  }, [canView, storeId, teamMemberId, t]);

  useEffect(() => {
    if (!permissionsLoading) {
      loadTeamMemberName();
    }
  }, [loadTeamMemberName, permissionsLoading]);

  if (loading || permissionsLoading) {
    return (
      <ManagementPageSkeleton
        pageTitle={t('employeePayments.compensation.title')}
        pageSubtitle={t('employeePayments.compensation.subtitle')}
        variant="form"
      />
    );
  }

  return (
    <ManagementPageLayout
      pageTitle={teamMemberName || t('employeePayments.compensation.title')}
      pageSubtitle={t('employeePayments.compensation.subtitle')}
      contentSx={{ maxWidth: { lg: '760px' }, width: { xs: '100%', lg: '760px' } }}
    >
      <ManagementPageCard>
        <Stack spacing={3} sx={{ p: { xs: 2.5, sm: 3, md: 4 } }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/management/stores/${storeId}/team-members/${teamMemberId}`)}
            variant="outlined"
            sx={{ alignSelf: 'flex-start', borderColor: BRAND.accent, color: BRAND.accentText }}
          >
            {t('common.back')}
          </Button>

          {loadError && <Alert severity="error">{loadError}</Alert>}

          {!loadError && (
            <TeamMemberCompensationForm
              storeId={storeId}
              teamMemberId={teamMemberId}
              canEdit={canEdit}
            />
          )}
        </Stack>
      </ManagementPageCard>
    </ManagementPageLayout>
  );
};

export default TeamMemberCompensationPage;
